import { CurrencyEnum } from '@codezo/graphql';
import { Box, Button, Group, Menu, Text, rem } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import React, { useMemo, useState } from 'react';
import { CURRENCY_INFO, useCurrencyStore } from '../stores/currency';

export interface CurrencySelectorProps {
	/** Override the selected currency (controlled mode) */
	value?: CurrencyEnum;
	/** Callback when currency changes */
	onChange?: (_currency: CurrencyEnum) => void;
	/** Custom popular currencies list. Defaults to USD, EUR, DKK, CNY */
	popularCurrencies?: CurrencyEnum[];
	/** Size of the selector */
	size?: 'xs' | 'sm' | 'md' | 'lg';
	/** Width of the dropdown */
	dropdownWidth?: number;
	/** Custom styling */
	style?: React.CSSProperties;
}

const DEFAULT_POPULAR_CURRENCIES = [
	CurrencyEnum.Usd,
	CurrencyEnum.Eur,
	CurrencyEnum.Dkk,
	CurrencyEnum.Cny,
];

export function CurrencySelector({
	value,
	onChange,
	popularCurrencies = DEFAULT_POPULAR_CURRENCIES,
	size = 'xs',
	dropdownWidth = 320,
	style,
}: CurrencySelectorProps) {
	const { selectedCurrency: storeSelectedCurrency, setCurrency: setStoreCurrency } =
		useCurrencyStore();
	const [menuOpened, setMenuOpened] = useState(false);

	// Use controlled value if provided, otherwise use store value
	const selectedCurrency = value ?? storeSelectedCurrency;
	const handleCurrencyChange = (currency: CurrencyEnum) => {
		if (onChange) {
			onChange(currency);
		} else {
			setStoreCurrency(currency);
		}
		setMenuOpened(false);
	};

	const currencyOptions = useMemo(() => {
		const popular = popularCurrencies.map((currency) => {
			const info = CURRENCY_INFO[currency];
			return {
				value: currency,
				label: `${info.flag} ${info.symbol} ${currency.toUpperCase()}`,
			};
		});

		const others = Object.values(CurrencyEnum)
			.filter((currency) => !popularCurrencies.includes(currency))
			.map((currency) => {
				const info = CURRENCY_INFO[currency];
				return {
					value: currency,
					label: `${info.flag} ${info.symbol} ${currency.toUpperCase()}`,
				};
			});

		return [
			{ group: 'Popular', items: popular },
			{ group: 'Other Currencies', items: others },
		];
	}, [popularCurrencies]);

	const selectedCurrencyInfo = CURRENCY_INFO[selectedCurrency];

	const buttonHeight = (() => {
		if (size === 'xs') return 28;
		if (size === 'sm') return 32;
		if (size === 'md') return 36;
		return 40;
	})();
	const fontSize = (() => {
		if (size === 'xs') return 11;
		if (size === 'sm') return 13;
		if (size === 'md') return 14;
		return 16;
	})();

	return (
		<Menu
			shadow="md"
			width={dropdownWidth}
			opened={menuOpened}
			onChange={setMenuOpened}
			position="bottom-end"
			styles={{
				dropdown: {
					maxHeight: rem(400),
					overflowY: 'auto',
				},
			}}
		>
			<Menu.Target>
				<Button
					variant="light"
					size={size}
					color="gray"
					style={{
						backgroundColor: 'var(--mantine-color-gray-0)',
						border: '1px solid var(--mantine-color-gray-3)',
						color: 'var(--mantine-color-gray-9)',
						padding: `${rem(6)} ${rem(12)}`,
						height: rem(buttonHeight),
						fontSize: rem(fontSize),
						fontWeight: 500,
						minWidth: rem(90),
						...style,
					}}
				>
					<Group gap={6} wrap="nowrap">
						<Text size={size} fw={500}>
							{selectedCurrencyInfo.symbol}
						</Text>
						<Text size={size} fw={500} c="dimmed">
							{selectedCurrency.toUpperCase()}
						</Text>
						<IconChevronDown size={12} />
					</Group>
				</Button>
			</Menu.Target>
			<Menu.Dropdown>
				<Menu.Label>Popular Currencies</Menu.Label>
				{currencyOptions[0].items.map((currency) => {
					const info = CURRENCY_INFO[currency.value];
					return (
						<Menu.Item
							key={currency.value}
							onClick={() => handleCurrencyChange(currency.value)}
							leftSection={<Text size="lg">{info.flag}</Text>}
						>
							<Box>
								<Text size="sm" fw={500}>
									{info.symbol} {currency.value.toUpperCase()}
								</Text>
								<Text size="xs" c="dimmed">
									{info.name}
								</Text>
							</Box>
						</Menu.Item>
					);
				})}
				<Menu.Divider />
				<Menu.Label>Other Currencies</Menu.Label>
				{currencyOptions[1].items.map((currency) => {
					const info = CURRENCY_INFO[currency.value];
					return (
						<Menu.Item
							key={currency.value}
							onClick={() => handleCurrencyChange(currency.value)}
							leftSection={<Text size="lg">{info.flag}</Text>}
						>
							<Box>
								<Text size="sm" fw={500}>
									{info.symbol} {currency.value.toUpperCase()}
								</Text>
								<Text size="xs" c="dimmed">
									{info.name}
								</Text>
							</Box>
						</Menu.Item>
					);
				})}
			</Menu.Dropdown>
		</Menu>
	);
}
